import React, { useEffect, useState } from "react";
import {
  FiSend,
  FiHome,
  FiSearch,
} from "react-icons/fi";
import { NavLink } from "react-router-dom";
import "./styles/BottomNavBar.css";
import { openAuth } from "../../../dataManagement/uiDb/GeneralDb";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../dataManagement/uiDb/UiDb";
import genericUtility from "../../../services/utility/GenericUtility";
import { getUserAvatarUrl } from "../../../services/helpers/ImageUrlHelper";
import { UserProfile } from "../../../interfaces/UserObject";
import { fetchLocalFinds } from "../../../services/helpers/SearchHelper";
import { setLocalFinds, setSearchString } from "../../../dataManagement/uiDb/ProductListDb";

const BottomNavBar: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();

    const profileDb: UserProfile | null = useSelector(
      (state: RootState) => state.profile.data,
    );

  const userPreferences = useSelector(
    (state: RootState) => state.profile.userPreferences,
  );

  // Count of conversations awaiting my reply (drives the message-icon badge).
  const yetToRespondCount = useSelector(
    (state: RootState) => (state.messages as any).yetToRespondCount as number,
  );

  // Profile-pic avatar for the nav (Instagram-style). Resolves the same Supabase
  // URL used elsewhere; falls back to the generic image if missing/unloadable.
  const [avatarError, setAvatarError] = useState(false);
  const navUserId = genericUtility.getCurrentUserId();
  const navAvatarUrl = navUserId ? getUserAvatarUrl(navUserId) : null;
  const navAvatarSrc =
    navAvatarUrl && !avatarError ? navAvatarUrl : "/default-profile-fallback.png";

  // Clear the broken-image latch when the signed-in user changes (logout/login,
  // account switch) so a new user's avatar gets a fresh chance to load.
  useEffect(() => {
    setAvatarError(false);
  }, [navUserId]);

  // Generic navigation handler that allows confirmation logic before navigating.
  function handleNavLinkClick(e: React.MouseEvent) {
    if (genericUtility.isUserLoggedIn() === false) {
      e.preventDefault();
      dispatch(openAuth());
      return;
    }
  }

  // Reels-style Local Finds entry: guard login, kick off the nearby fetch,
  // then let the NavLink route to /ninefinds.
  function handleLocalFindsClick(e: React.MouseEvent) {
    if (genericUtility.isUserLoggedIn() === false) {
      e.preventDefault();
      dispatch(openAuth());
      return;
    }
    fetchLocalFinds(dispatch, userPreferences);
    dispatch(setSearchString(""));
    dispatch(setLocalFinds());
  }

    function handleMessageNavLinkClick(e: React.MouseEvent) {
    if (genericUtility.isUserLoggedIn() === false) {
      e.preventDefault();
      dispatch(openAuth());
      return;
    }
  }

  // Notification permission is centralized in useNotificationPermission (run by
  // FirebaseNotificationsProvider) and surfaced via gesture-based prompts (the
  // Messages banner / ProfileMenu toggle). No on-mount auto-prompt here.

  return (
    <nav className="bottom-nav-bar">
      <NavLink
        to="/"
        className={({ isActive }) =>
          `bottom-nav-link${isActive ? " active" : ""}`
        }
        end
      >
        <FiHome />
      </NavLink>
      <NavLink
        to="/ninefinds"
        className={({ isActive }) =>
          `bottom-nav-link${isActive ? " active" : ""}`
        }
        onClick={(e) => handleLocalFindsClick(e)}
      >
        <img
          src="/nine_logo_vector_4.svg"
          alt="Local Finds"
          className="bottom-nav-logo-icon"
        />
      </NavLink>
      <NavLink
        to="/my-messages"
        className={({ isActive }) =>
          `bottom-nav-link${isActive ? " active" : ""}`
        }
        onClick={(e) => handleMessageNavLinkClick(e)}
      >
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <FiSend />
          {yetToRespondCount > 0 && (
            <span className="message-badge" key={yetToRespondCount}>
              {yetToRespondCount > 99 ? "99+" : yetToRespondCount}
            </span>
          )}
        </div>
      </NavLink>
      <NavLink
        to="/search"
        className={({ isActive }) =>
          `bottom-nav-link${isActive ? " active" : ""}`
        }
      >
        <FiSearch />
      </NavLink>
      <NavLink
        to="/my-listings"
        className={({ isActive }) =>
          `bottom-nav-link${isActive ? " active" : ""}`
        }
        onClick={(e) => handleNavLinkClick(e)}
      >
        <img
          src={navAvatarSrc}
          alt="Profile"
          className="bottom-nav-avatar"
          onError={() => setAvatarError(true)}
        />
      </NavLink>
    </nav>
  );
};

export default BottomNavBar;
